import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowLeft,
  faArrowsRotate,
  faCheck,
  faClock,
  faCopy,
  faEnvelope,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { partnerApi } from '../../services/api/partnerApi'
import type { PartnerPendingCustomerDetail } from '../../types/partner'

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function PendingCustomerDetail() {
  const { pendingAccountId } = useParams<{ pendingAccountId: string }>()
  const navigate = useNavigate()
  const [detail, setDetail] = useState<PartnerPendingCustomerDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [actionMessage, setActionMessage] = useState<string | null>(null)
  const [regenerating, setRegenerating] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!pendingAccountId) return
      setLoading(true)
      setError(null)
      try {
        const data = await partnerApi.getPendingCustomer(pendingAccountId)
        if (!cancelled) setDetail(data)
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load pending invite')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [pendingAccountId])

  const handleCopy = async () => {
    if (!detail?.registrationUrl) return
    try {
      await navigator.clipboard.writeText(detail.registrationUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy invite link', err)
    }
  }

  const handleRegenerate = async () => {
    if (!detail) return
    if (
      !window.confirm(
        'Generate a new registration link? The previous link will stop working immediately.',
      )
    ) {
      return
    }
    setRegenerating(true)
    setActionMessage(null)
    try {
      const res = await partnerApi.regenerateRegistrationLink(detail.pendingAccountId)
      setDetail({
        ...detail,
        registrationUrl: res.registrationUrl,
        tokenExpiresAt: res.tokenExpiresAt,
        expiresAt: res.expiresAt,
      })
      setActionMessage('New registration link generated. The old link will no longer work.')
      setCopied(false)
    } catch (e: any) {
      setActionMessage(e?.message || 'Failed to generate new registration link')
    } finally {
      setRegenerating(false)
    }
  }

  const handleCancel = async () => {
    if (!detail) return
    if (!window.confirm(`Cancel the invitation for ${detail.email}?`)) return
    setCancelling(true)
    setActionMessage(null)
    try {
      await partnerApi.cancelInvite(detail.pendingAccountId)
      navigate('/partner/customers')
    } catch (e: any) {
      setActionMessage(e?.message || 'Failed to cancel invite')
      setCancelling(false)
    }
  }

  if (loading && !detail) {
    return <div className="p-6 text-gray-500">Loading…</div>
  }
  if (error || !detail) {
    return (
      <div className="p-6">
        <p className="text-red-600">{error || 'Pending invite not found'}</p>
        <Link to="/partner/customers" className="text-blue-600 hover:underline mt-3 inline-block">
          Back to customers
        </Link>
      </div>
    )
  }

  const tokenExpired = detail.tokenExpiresAt
    ? new Date(detail.tokenExpiresAt) < new Date()
    : !detail.registrationUrl

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
      <div>
        <Link
          to="/partner/customers"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Customers
        </Link>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">{detail.email}</h1>
            <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500">
              <span className="inline-flex items-center gap-1 text-yellow-700 font-medium">
                <FontAwesomeIcon icon={faClock} className="h-3 w-3" /> Pending registration
              </span>
              <span>Invited: {formatDate(detail.invitedAt)}</span>
              <span>Invite expires: {formatDate(detail.expiresAt)}</span>
              <span>Staged devices: {detail.stagedDevices.length}</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleCancel}
              disabled={cancelling}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faXmark} />
              {cancelling ? 'Cancelling…' : 'Cancel invite'}
            </button>
          </div>
        </div>
        {actionMessage && (
          <p className="mt-3 text-sm text-red-700">{actionMessage}</p>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-medium flex items-center gap-2">
            <FontAwesomeIcon icon={faEnvelope} className="text-blue-600" />
            Registration link
          </h2>
          <button
            type="button"
            onClick={handleRegenerate}
            disabled={regenerating}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50"
            title="Issue a new registration link (the old one will stop working)"
          >
            <FontAwesomeIcon icon={faArrowsRotate} className={regenerating ? 'animate-spin' : ''} />
            {regenerating ? 'Generating…' : 'Generate new link'}
          </button>
        </div>
        {actionMessage && (
          <p className="mt-2 text-sm text-blue-700">{actionMessage}</p>
        )}
        {detail.registrationUrl && !tokenExpired ? (
          <>
            <p className="mt-1 text-sm text-gray-600">
              Share this link with the customer so they can finish registering. Token expires{' '}
              {formatDate(detail.tokenExpiresAt)}.
            </p>
            <div className="mt-3 flex gap-2">
              <input
                type="text"
                readOnly
                value={detail.registrationUrl}
                onFocus={(e) => e.currentTarget.select()}
                onClick={(e) => e.currentTarget.select()}
                className="flex-1 px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg bg-gray-50 focus:bg-white focus:ring-1 focus:ring-blue-500 focus:outline-none"
              />
              <button
                type="button"
                onClick={handleCopy}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 inline-flex items-center gap-2"
              >
                <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </>
        ) : (
          <p className="mt-1 text-sm text-gray-600">
            The registration token has expired or was never generated. Use “Generate new link” above to issue a fresh one.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <h2 className="text-lg font-medium">Staged RICs</h2>
        {detail.stagedDevices.length === 0 ? (
          <p className="text-sm text-gray-500">
            No RICs staged for this invite.
          </p>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
                <tr>
                  <th className="px-4 py-2 text-left">Sigfox ID</th>
                  <th className="px-4 py-2 text-left">Serial</th>
                  <th className="px-4 py-2 text-left">HW</th>
                  <th className="px-4 py-2 text-left">Group (staged)</th>
                  <th className="px-4 py-2 text-left">Sigfox</th>
                </tr>
              </thead>
              <tbody>
                {detail.stagedDevices.map((d) => (
                  <tr key={d.deviceId} className="border-t border-gray-100">
                    <td className="px-4 py-2 font-mono">{d.wisolId}</td>
                    <td className="px-4 py-2">{d.serialNumber ?? '—'}</td>
                    <td className="px-4 py-2">{d.hwVersion ?? '—'}</td>
                    <td className="px-4 py-2">{d.stagedGroupName || '—'}</td>
                    <td className="px-4 py-2">{d.sigfoxRegistered ? 'Y' : 'N'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
